import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { get, patch, post } from '../../../api/apiClient';
import { Eye, Search, X, CheckCircle, XCircle, AlertCircle, Clock, Plus, Download } from 'lucide-react';
import ActionConfirmationModal from '../../../components/hrms/ActionConfirmationModal';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import WfhDetailsModal from '../../../components/common/WfhDetailsModal';
import WfhApplyModal from '../../../components/common/WfhApplyModal';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import Button from '../../../components/common/Button/Button';
import useUrlTableState from '../../../hooks/useUrlTableState';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

import { formatDate as stringUtilsFormatDate } from '../../../utils/stringUtils';

const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  return stringUtilsFormatDate(dateValue, { includeTime: false });
};

const getEmployeeName = (item) => {
  if (!item?.employeeId) return 'Employee';
  if (item.employeeId.personalInfo) {
    return `${item.employeeId.personalInfo.firstName || ''} ${item.employeeId.personalInfo.lastName || ''}`.trim() || 'Employee';
  }
  return 'Employee';
};

const WorkFromHomeApprovals = () => {
  const queryClient = useQueryClient();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    fromDate: '',
    toDate: ''
  }), []);

  const {
    filters: wfhFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateWfhFilter,
    updatePage: updateWfhPage,
    updateLimit: updateWfhLimit,
    resetFilters: resetWfhFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const statusFilter = wfhFilters.status || 'all';
  const setStatusFilter = useCallback((status) => {
    updateWfhFilter({ status });
  }, [updateWfhFilter]);

  const filterStartDate = wfhFilters.fromDate || '';
  const filterEndDate = wfhFilters.toDate || '';

  const setDateRange = useCallback((range) => {
    updateWfhFilter({ fromDate: range.startDate, toDate: range.endDate });
  }, [updateWfhFilter]);

  const currentPage = wfhFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateWfhPage(page);
  }, [updateWfhPage]);

  const itemsPerPage = wfhFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateWfhLimit(limit);
  }, [updateWfhLimit]);

  const [actionLoadingId, setActionLoadingId] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Reusable Confirmation Modal States
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState('');
  const [confirmActionType, setConfirmActionType] = useState('');

  // Assign WFH States
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Row selection state
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Query: WFH Requests
  const { data: wfhData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['wfh-requests', statusFilter, currentPage, itemsPerPage, filterStartDate, filterEndDate],
    queryFn: async () => {
      let url = `/work-from-home/admin?page=${currentPage}&limit=${itemsPerPage}`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (filterStartDate) {
        url += `&fromDate=${filterStartDate}`;
      }
      if (filterEndDate) {
        url += `&toDate=${filterEndDate}`;
      }
      const response = await get(url);
      if (response?.success || response?.responseCode === 200) {
        return response?.data || { requests: [], pagination: {} };
      }
      throw new Error('Failed to load WFH requests.');
    }
  });

  const requests = wfhData?.requests || [];
  const pagination = wfhData?.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: itemsPerPage };

  // Filter requests locally by Employee Name, Request ID, and Date Range
  const filteredRequests = useMemo(() => {
    let result = requests;

    // Filter by search term
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(r => {
        const empName = getEmployeeName(r).toLowerCase();
        const requestId = (r.requestId || '').toLowerCase();
        return empName.includes(lower) || requestId.includes(lower);
      });
    }

    // Filter by date range
    if (filterStartDate || filterEndDate) {
      result = result.filter(r => {
        if (!r.fromDate) return false;

        const requestFromDate = new Date(r.fromDate);
        const requestToDate = r.toDate ? new Date(r.toDate) : requestFromDate;

        // Parse filter dates (assuming YYYY-MM-DD format)
        const filterStart = filterStartDate ? new Date(filterStartDate) : null;
        const filterEnd = filterEndDate ? new Date(filterEndDate) : null;

        // Normalize dates to compare only the date part (ignore time)
        const normalizeDate = (date) => {
          const normalized = new Date(date);
          normalized.setHours(0, 0, 0, 0);
          return normalized;
        };

        const normalizedRequestFrom = normalizeDate(requestFromDate);
        const normalizedRequestTo = normalizeDate(requestToDate);
        const normalizedFilterStart = filterStart ? normalizeDate(filterStart) : null;
        const normalizedFilterEnd = filterEnd ? normalizeDate(filterEnd) : null;

        // Check if the request date range overlaps with the filter range
        if (normalizedFilterStart && normalizedRequestTo < normalizedFilterStart) return false;
        if (normalizedFilterEnd && normalizedRequestFrom > normalizedFilterEnd) return false;

        return true;
      });
    }

    return result;
  }, [requests, searchTerm, filterStartDate, filterEndDate]);



  const error = queryError?.message || '';

  // Query: Employees List (handled inside modal now)

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user_data');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setCurrentUser(parsed);
    } catch (err) {
      setCurrentUser(null);
    }
  }, []);

  const handleDecisionClick = (requestId, actionType) => {
    setConfirmTargetId(requestId);
    setConfirmActionType(actionType);
    setShowConfirmModal(true);
  };

  // Mutation: Decision (Approve/Reject)
  const decisionMutation = useMutation({
    mutationFn: async ({ requestId, decision, comments }) => {
      return await patch(`/work-from-home/${requestId}/decision`, {
        decision,
        comments
      });
    },
    onMutate: ({ requestId }) => {
      setActionLoadingId(requestId);
    },
    onSuccess: (_, { decision }) => {
      toast.success(`WFH request ${decision} successfully`);
      queryClient.invalidateQueries({ queryKey: ['wfh-requests'] });
      setShowConfirmModal(false);
    },
    onError: (err) => {
      toast.error(err?.message || `Failed to process WFH request.`);
    },
    onSettled: () => {
      setActionLoadingId('');
    }
  });

  const handleDecisionConfirm = async (comments) => {
    const decision = confirmActionType === 'approve' ? 'approved' : 'rejected';
    decisionMutation.mutate({
      requestId: confirmTargetId,
      decision,
      comments
    });
  };

  // Mutation: Assign WFH
  const assignMutation = useMutation({
    mutationFn: async (payload) => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/work-from-home/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || 'Failed to assign WFH.');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('WFH assigned successfully');
      setShowAssignModal(false);
      queryClient.invalidateQueries({ queryKey: ['wfh-requests'] });
    },
    onError: (err) => {
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.responseData?.message || err.responseData?.error || err.message || 'Failed to assign WFH.';
      toast.error(errMsg);
    }
  });

  const assignSubmitting = assignMutation.isPending;

  const openDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setSelectedRequest(null);
    setShowDetails(false);
  };

  const getCurrentApproverStage = (item) => {
    const role = currentUser?.role;
    const userId = currentUser?._id || currentUser?.id;
    if (!role) return null;
    if (role === 'super_admin') return 'superAdmin';
    if (role === 'hr_manager' || role === 'hr_executive') return 'hr';
    const repManager = item?.employeeId?.employmentInfo?.reportingManager;
    const managerUserId = repManager?.user || repManager;
    if (managerUserId && (managerUserId === userId || managerUserId._id === userId)) {
      return 'manager';
    }
    return null;
  };

  const canTakeDecision = (item) => {
    if (item?.status !== 'pending') return false;
    const stage = getCurrentApproverStage(item);
    if (!stage) return false;
    return item?.approvals?.status === 'pending';
  };

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Build base export filters
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      fromDate: filterStartDate || undefined,
      toDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, statusFilter, filterStartDate, filterEndDate]);

  // Get current page request IDs
  const currentPageRequestIds = useMemo(
    () =>
      filteredRequests
        .map((r) => {
          const raw = r?._id ?? r?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredRequests]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageRequestIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'work-from-home',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortField,
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `work-from-home-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortField,
      sortOrder,
      currentPageRequestIds,
    ]
  );

  // Handle export for selected requests with format selection
  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected requests to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal
  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageRequestIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      filteredRequests.length,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageRequestIds,
    ]
  );

  // Export click handler
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Reset row selections when filters, search, pagination change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [statusFilter, searchTerm, currentPage, itemsPerPage, filterStartDate, filterEndDate]);

  return (
    <div className="flex flex-col gap-0">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">Work From Home Approvals</h2>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-brand/5 border-brand/20 text-brand' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected requests`
                    : 'Export requests'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleSelectedFormatExport}
              />
            </div>
            {currentUser && (currentUser.role === 'super_admin' || currentUser.role === 'hr_manager' || currentUser.role === 'hr_executive') && (
              <button
                onClick={() => setShowAssignModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand/90 transition-colors shadow-sm"
                style={{ backgroundColor: '#3b82f6' }}
              >
                <Plus className="h-4 w-4" />
                Assign WFH
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mx-0">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by employee name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.slice(0, 100))}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-blue-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-blue-400 text-sm"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <DateRangeFilter
            startDate={filterStartDate}
            endDate={filterEndDate}
            onChange={setDateRange}
          />

          {/* Clear Filters */}
          {(searchTerm || statusFilter !== 'all' || filterStartDate || filterEndDate) && (
            <button
              onClick={() => { resetWfhFilters(); }}
              className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-blue-50/30 transition-all shadow-sm"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full divide-y divide-gray-200">
          <thead style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[120px]">Request ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[160px]">Employee</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[110px]">From</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[110px]">To</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50">Days</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[130px]">Approved By</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[100px]">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[130px]">Created</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider bg-gray-50 min-w-[150px]">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <span className="text-gray-500">Loading WFH requests...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center text-rose-500 text-sm">{error}</td>
              </tr>
            ) : filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center text-gray-500 text-sm">
                  No WFH requests found.
                </td>
              </tr>
            ) : (
              filteredRequests.map((item) => {
                const approvedBy = [];
                if (item?.approvals?.status === 'approved') {
                  const approverName = item.approvals.actedBy?.firstName
                    ? `${item.approvals.actedBy.firstName} ${item.approvals.actedBy.lastName || ''}`.trim()
                    : 'System';
                  approvedBy.push(approverName);
                }
                return (
                  <tr key={item._id} className="hover:bg-blue-50/30">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">{item.requestId}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{getEmployeeName(item)}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(item.fromDate)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(item.toDate)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{item.totalDays}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{approvedBy.length ? approvedBy.join(', ') : '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {item.status === 'approved' && <CheckCircle className="h-4 w-4 text-green-500" />}
                        {item.status === 'rejected' && <XCircle className="h-4 w-4 text-red-500" />}
                        {item.status === 'pending' && <AlertCircle className="h-4 w-4 text-yellow-500" />}
                        <span className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${item.status === 'approved' ? 'bg-green-100 text-green-800' :
                          item.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{stringUtilsFormatDate(item.createdAt, { includeTime: true })}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openDetails(item)}
                          className="p-1.5 text-blue-500 hover:text-[#0B2033] hover:bg-blue-50 rounded transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {canTakeDecision(item) && (
                          <>
                            <button
                              onClick={() => handleDecisionClick(item._id, 'approve')}
                              disabled={actionLoadingId === item._id}
                              className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              title="Approve"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDecisionClick(item._id, 'reject')}
                              disabled={actionLoadingId === item._id}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {/* Pagination - integrated into the table card */}
        <PaginationControls
          currentPage={pagination.currentPage || 1}
          totalPages={pagination.totalPages || 1}
          totalItems={pagination.totalItems || 0}
          limit={itemsPerPage}
          hasNextPage={currentPage < (pagination.totalPages || 1)}
          hasPrevPage={currentPage > 1}
          onPageChange={(page) => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onLimitChange={(newLimit) => {
            setItemsPerPage(newLimit);
          }}
          limitOptions={[10, 25, 50, 100]}
          border={false}
        />
      </div>

      {/* Details Modal */}
      <WfhDetailsModal
        isOpen={showDetails}
        onClose={closeDetails}
        request={selectedRequest}
        formatDate={formatDate}
        getEmployeeName={getEmployeeName}
      />

      {/* ActionConfirmationModal */}
      <ActionConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={handleDecisionConfirm}
        title={confirmActionType === 'approve' ? 'Approve Request' : 'Reject Request'}
        actionType={confirmActionType}
        actionLoading={actionLoadingId !== ''}
      />

      {/* Assign WFH Modal */}
      <WfhApplyModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onSubmit={(payload) => assignMutation.mutate(payload)}
        isAssign={true}
        submitting={assignSubmitting}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Work From Home Requests"
        showDateRange
        scopedFormatExport={{
          onExport: handleScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default WorkFromHomeApprovals;
